import { logger } from '@/lib/logger';

type SupabaseConfig = {
  url: string;
  publishableKey: string;
};

type ElevenLabsConfig = {
  apiKey: string;
  voiceId: string | undefined;
  modelId: string | undefined;
  agentId: string | undefined;
  dynamicVariables?: Record<string, string | number | boolean>;
};

type ElevenLabsWebhookConfig = {
  secret: string;
  previousSecret?: string | undefined;
};

type ConfigResult<TConfig> =
  { config: TConfig; missing: null } | { config: null; missing: string[] };

function collectConfig<TConfig>(
  resolve: () => { config: TConfig; missing: string[] }
): ConfigResult<TConfig> {
  const { config, missing } = resolve();

  if (missing.length > 0) {
    return { config: null, missing };
  }

  return { config, missing: null };
}

export function readSupabaseConfig(): ConfigResult<SupabaseConfig> {
  return collectConfig(() => {
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const publishableKey = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY;

    const missing: string[] = [];

    if (!url) missing.push('NEXT_PUBLIC_SUPABASE_URL');
    if (!publishableKey) missing.push('NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY');

    return {
      config: {
        url: url!,
        publishableKey: publishableKey!,
      } satisfies SupabaseConfig,
      missing,
    };
  });
}

export function readElevenLabsConfig(): ConfigResult<ElevenLabsConfig> {
  return collectConfig(() => {
    const apiKey = process.env.ELEVENLABS_API_KEY;
    const voiceId = process.env.ELEVENLABS_VOICE_ID;
    const modelId = process.env.ELEVENLABS_MODEL_ID;

    // Try to get agent ID from agents.json (environment-aware)
    // Falls back to ELEVENLABS_AGENT_ID env var for backward compatibility
    let agentId: string | undefined;

    // Only try to load from agents.json on the server (fs module not available in browser)
    if (typeof window === 'undefined') {
      try {
        // Dynamic import to avoid issues in edge runtime
        const { getAgentIdForEnvironment } = require('@/lib/elevenlabs/config');
        agentId = getAgentIdForEnvironment();
      } catch (error) {
        // Fallback to env var if agents.json loading fails
        agentId = process.env.ELEVENLABS_AGENT_ID;
        logger.warn('[ElevenLabs] Failed to load agent from agents.json, using env var:', {
          error,
        });
      }
    } else {
      // Client-side: always use env var
      agentId = process.env.ELEVENLABS_AGENT_ID;
    }

    const missing: string[] = [];

    const usingAgent = Boolean(agentId);

    if (!apiKey) missing.push('ELEVENLABS_API_KEY');
    if (!usingAgent) {
      if (!voiceId) missing.push('ELEVENLABS_VOICE_ID');
      if (!modelId) missing.push('ELEVENLABS_MODEL_ID');
    }

    return {
      config: {
        apiKey: apiKey!,
        voiceId,
        modelId,
        agentId,
      } satisfies ElevenLabsConfig,
      missing,
    };
  });
}

/**
 * Post-call webhook HMAC key (`ELEVENLABS_WEBHOOK_SECRET`).
 *
 * `previousSecret` exists only during an HMAC rotation (#5120): no reconciler re-fetches a
 * rejected post-call webhook, so the outgoing key stays valid until the agents point at the
 * new workspace webhook. Unset it after.
 */
export function readElevenLabsWebhookConfig(): ConfigResult<ElevenLabsWebhookConfig> {
  return collectConfig(() => {
    const secret = process.env.ELEVENLABS_WEBHOOK_SECRET;

    const missing: string[] = [];
    if (!secret) missing.push('ELEVENLABS_WEBHOOK_SECRET');

    return {
      config: {
        secret: secret!,
        previousSecret: process.env.ELEVENLABS_WEBHOOK_SECRET_PREVIOUS || undefined,
      } satisfies ElevenLabsWebhookConfig,
      missing,
    };
  });
}

/**
 * The shared secret ElevenLabs sends in `X-ElevenLabs-Webhook-Secret` on tool calls and
 * the conversation-initiation webhook.
 *
 * WHY separate from `ELEVENLABS_WEBHOOK_SECRET` (#5120): that var is the post-call HMAC
 * key. It used to double as this header secret, and ElevenLabs echoes tool-call headers
 * into transcripts, so one leak handed out both. Never fall back to it.
 *
 * `previous` exists only during a rotation: CI pushes tool configs (all envs, on any
 * staging deploy) and the prod agent's initiation header (on promotion) at different
 * times, so the runtime accepts the outgoing value until both have moved. Unset it after.
 */
export type ToolSecrets = { current?: string | undefined; previous?: string | undefined };

export function readElevenLabsToolSecrets(): ToolSecrets {
  return {
    current: process.env.ELEVENLABS_TOOL_SECRET || undefined,
    previous: process.env.ELEVENLABS_TOOL_SECRET_PREVIOUS || undefined,
  };
}

/**
 * Scribe v2 batch webhook secret — ElevenLabs issues a separate signing secret
 * for the Speech-to-Text product (distinct from the Conv AI post-call secret
 * read above). Falls back to `ELEVENLABS_WEBHOOK_SECRET` only when the
 * Scribe-specific var is unset, so existing single-secret setups stay working.
 */
export function readElevenLabsScribeWebhookConfig(): ConfigResult<ElevenLabsWebhookConfig> {
  return collectConfig(() => {
    const secret =
      process.env.ELEVENLABS_SCRIBE_WEBHOOK_SECRET ?? process.env.ELEVENLABS_WEBHOOK_SECRET;

    const missing: string[] = [];
    if (!secret) missing.push('ELEVENLABS_SCRIBE_WEBHOOK_SECRET');

    return {
      config: {
        secret: secret!,
      } satisfies ElevenLabsWebhookConfig,
      missing,
    };
  });
}

/**
 * Scribe kickoff webhook target — the workspace webhook id (from the
 * ElevenLabs dashboard's webhook list) that async Speech-to-Text results
 * should be delivered to. Without it, ElevenLabs fans the completion event
 * out to EVERY webhook subscribed to speech_to_text in the shared workspace,
 * which crosses environments: a staging Scribe run 400s against the prod
 * post-call endpoint (PostHog issue 01a01fd4, 2026-08-20). Optional so local
 * dev keeps working, but staging and prod Doppler configs must set it.
 * `||` (not `??`) is deliberate: a blank Doppler value means "unset", so the
 * kickoff warns instead of sending an empty webhook_id. Trimmed for the same
 * reason: a whitespace-only value must also fall through to "unset" rather
 * than being sent to ElevenLabs as a garbage webhook_id.
 */
export function readElevenLabsScribeWebhookId(): string | undefined {
  return process.env.ELEVENLABS_SCRIBE_WEBHOOK_ID?.trim() || undefined;
}

export type StripeConfig = {
  secretKey: string;
  webhookSecret: string;
  priceMonthlyId: string;
  priceAnnualId?: string;
  priceAnnualEssentialId?: string;
  priceAnnualKeepsakeId?: string;
  priceAnnualFamilyId?: string;
  priceBookEssentialId?: string;
  priceBookKeepsakeId?: string;
  priceBookFamilyId?: string;
  priceBookStandardId?: string;
  /**
   * The StoryWorth campaign hardcover's own Stripe price. Optional like its siblings, but
   * unlike them it is expected to be ABSENT until the owner creates the price in Stripe and
   * sets it in Doppler — campaign books fall back to the standard price (and the standard
   * label) together until then. `resolveCampaignBookPrice` owns that fallback; the one-off
   * catalog (`entitlements/one-off-products.ts`) also names this key so the webhook grant
   * maps it to a book credit.
   */
  priceBookCampaignId?: string;
  priceMailedInviteAddonId?: string;
};

export function readStripeConfig(): ConfigResult<StripeConfig> {
  return collectConfig(() => {
    const secretKey = process.env.STRIPE_SECRET_KEY;
    const webhookSecret = process.env.STRIPE_WEBHOOK_SECRET;
    const priceMonthlyId = process.env.STRIPE_PRICE_MONTHLY_ID;
    const priceAnnualId = process.env.STRIPE_PRICE_ANNUAL_ID;
    const priceAnnualEssentialId = process.env.STRIPE_PRICE_ANNUAL_ESSENTIAL_ID;
    const priceAnnualKeepsakeId = process.env.STRIPE_PRICE_ANNUAL_KEEPSAKE_ID;
    const priceAnnualFamilyId = process.env.STRIPE_PRICE_ANNUAL_FAMILY_ID;
    const priceBookEssentialId = process.env.STRIPE_PRICE_BOOK_ESSENTIAL_ID;
    const priceBookKeepsakeId = process.env.STRIPE_PRICE_BOOK_KEEPSAKE_ID;
    const priceBookFamilyId = process.env.STRIPE_PRICE_BOOK_FAMILY_ID;
    const priceBookStandardId = process.env.STRIPE_PRICE_BOOK_STANDARD_ID;
    const priceBookCampaignId = process.env.STRIPE_PRICE_BOOK_CAMPAIGN_ID;
    const priceMailedInviteAddonId = process.env.STRIPE_PRICE_MAILED_INVITE_ADDON_ID;

    const missing: string[] = [];
    if (!secretKey) missing.push('STRIPE_SECRET_KEY');
    if (!webhookSecret) missing.push('STRIPE_WEBHOOK_SECRET');
    if (!priceMonthlyId) missing.push('STRIPE_PRICE_MONTHLY_ID');

    return {
      config: {
        secretKey: secretKey!,
        webhookSecret: webhookSecret!,
        priceMonthlyId: priceMonthlyId!,
        ...(priceAnnualId ? { priceAnnualId } : {}),
        ...(priceAnnualEssentialId ? { priceAnnualEssentialId } : {}),
        ...(priceAnnualKeepsakeId ? { priceAnnualKeepsakeId } : {}),
        ...(priceAnnualFamilyId ? { priceAnnualFamilyId } : {}),
        ...(priceBookEssentialId ? { priceBookEssentialId } : {}),
        ...(priceBookKeepsakeId ? { priceBookKeepsakeId } : {}),
        ...(priceBookFamilyId ? { priceBookFamilyId } : {}),
        ...(priceBookStandardId ? { priceBookStandardId } : {}),
        ...(priceBookCampaignId ? { priceBookCampaignId } : {}),
        ...(priceMailedInviteAddonId ? { priceMailedInviteAddonId } : {}),
      } satisfies StripeConfig,
      missing,
    };
  });
}

type PostgridConfig = { apiKey?: string; testApiKey: string };

export function readPostgridConfig(): ConfigResult<PostgridConfig> {
  return collectConfig(() => {
    const apiKey = process.env.POSTGRID_API_KEY;
    const testApiKey = process.env.POSTGRID_TEST_API_KEY;

    const missing: string[] = [];
    if (!testApiKey) missing.push('POSTGRID_TEST_API_KEY'); // required everywhere

    return {
      config: { testApiKey: testApiKey!, ...(apiKey ? { apiKey } : {}) } satisfies PostgridConfig,
      missing,
    };
  });
}

type PostgridAddverConfig = { apiKey?: string; testApiKey: string };

/** Address Verification is a separate PostGrid product from Print & Mail with its own
 *  keys (#3021: a print-mail key gets HTTP 401 from the addver API). Kept independent of
 *  readPostgridConfig so a missing addver key can't break mail-invite, and vice versa. */
export function readPostgridAddverConfig(): ConfigResult<PostgridAddverConfig> {
  return collectConfig(() => {
    const apiKey = process.env.POSTGRID_ADDVER_API_KEY;
    const testApiKey = process.env.POSTGRID_ADDVER_TEST_API_KEY;

    const missing: string[] = [];
    if (!testApiKey) missing.push('POSTGRID_ADDVER_TEST_API_KEY'); // required everywhere

    return {
      config: {
        testApiKey: testApiKey!,
        ...(apiKey ? { apiKey } : {}),
      } satisfies PostgridAddverConfig,
      missing,
    };
  });
}

export interface LuluConfig {
  apiBase: string;
  clientKey: string;
  clientSecret: string;
  podPackageId: string;
}

export function readLuluConfig(): ConfigResult<LuluConfig> {
  return collectConfig(() => {
    const apiBase = process.env.LULU_API_BASE;
    const clientKey = process.env.LULU_CLIENT_KEY;
    const clientSecret = process.env.LULU_CLIENT_SECRET;
    const podPackageId = process.env.LULU_POD_PACKAGE_ID;

    const missing: string[] = [];
    if (!apiBase) missing.push('LULU_API_BASE');
    if (!clientKey) missing.push('LULU_CLIENT_KEY');
    if (!clientSecret) missing.push('LULU_CLIENT_SECRET');
    if (!podPackageId) missing.push('LULU_POD_PACKAGE_ID');

    return {
      config: {
        apiBase: apiBase!,
        clientKey: clientKey!,
        clientSecret: clientSecret!,
        podPackageId: podPackageId!,
      } satisfies LuluConfig,
      missing,
    };
  });
}

export function readSiteUrl(): string | undefined {
  return process.env.NEXT_PUBLIC_SITE_URL;
}

type PolicyAgreementConfig = {
  gracePeriodDays: number;
  emailBatchWindowHours: number;
  policyMaxFileSize: number;
};

export function readPolicyAgreementConfig(): PolicyAgreementConfig {
  const gracePeriodDays = parseInt(process.env.AGREEMENT_GRACE_PERIOD_DAYS || '7', 10);
  const emailBatchWindowHours = parseInt(process.env.EMAIL_BATCH_WINDOW_HOURS || '2', 10);
  const policyMaxFileSize = parseInt(process.env.POLICY_MAX_FILE_SIZE || '1048576', 10);

  return {
    gracePeriodDays,
    emailBatchWindowHours,
    policyMaxFileSize,
  };
}

/**
 * Exactly one bare address on Trustpilot's AFS domain. Anchored and whitespace/comma-free
 * so a multi-address string ("a@evil.com,b@invite.trustpilot.com") cannot smuggle a
 * foreign recipient past the check.
 */
const TRUSTPILOT_AFS_ADDRESS = /^[^\s@,;<>]+@invite\.trustpilot\.com$/i;

/**
 * Reads TRUSTPILOT_AFS_BCC_EMAIL — the Trustpilot Automatic Feedback Service address
 * that, when BCC'd on a transactional email, makes Trustpilot send the customer a
 * verified review invitation after a dashboard-configured delay.
 *
 * Set ONLY in the production Doppler config. Staging shares the production Lulu
 * account, so staging test orders produce real shipped emails; leaving this unset
 * there is what keeps them from generating invitations. Unset, blank, or
 * whitespace-only means "off" (`||`-style, matching readElevenLabsScribeWebhookId).
 *
 * The address is a capability — anyone holding it can trigger invitations from our
 * profile — so it is treated as a secret, and anything that is not a single address
 * on the AFS domain is ignored with a warning rather than risk BCC'ing customer
 * email to a stranger. Logged at `warn`, not `error`: a mistyped secret is an
 * operator mistake, not a defect for error tracking.
 *
 * Server-only: reads process.env.
 */
export function readTrustpilotAfsBccEmail(): string | undefined {
  const value = process.env.TRUSTPILOT_AFS_BCC_EMAIL?.trim();
  if (!value) return undefined;

  if (!TRUSTPILOT_AFS_ADDRESS.test(value)) {
    const at = value.lastIndexOf('@');
    logger.warn(
      '[env] TRUSTPILOT_AFS_BCC_EMAIL is not a single @invite.trustpilot.com address; ignoring it',
      // Only the domain: the local part is the secret half of the address.
      { domain: at >= 0 ? value.slice(at) : '(none)' }
    );
    return undefined;
  }

  return value;
}
